import type { Command } from "../document/commands.ts";
import type { PropInstance, WorldDocument } from "../document/types.ts";
import { pullFramesCommand } from "../editor/packs.ts";
import { isTilePropsTileset } from "./types.ts";

/**
 * The placement of a prop made from tiles (ADR-102 decision 8). The open world may not list the pack's tile props
 * tileset yet, since a pack's first entry finds it without one, so the placement carries the tileset in with it as
 * one compound command: a world operation, recorded in history and sent over the live stream like any other, and
 * taken back with the placement by one undo. The tileset enters as a pick of the frame placed (ADR-129 decision
 * 3.4, ADR-130 decision 1.4), at the end of the list so no packed tile cell moves.
 */

/**
 * The command that places a prop whose frame is an entry of a pack's tile props.
 *
 * @param world The document.
 * @param mapId The map to place it on.
 * @param layerId The prop layer.
 * @param index The prop's place in the layer's list.
 * @param prop The prop; its frame is a tile of a `<pack>:tile-props` tileset.
 * @returns A compound of the tileset pull and the placement when the world lacks the tileset or the frame, and the
 *   placement alone when it lists the tileset whole or already holds the frame.
 * @throws Error when the frame is not of a tile props tileset, or the pull is refused for a pick cap.
 */
export function placeTilePropCommand(world: WorldDocument, mapId: string, layerId: string, index: number, prop: PropInstance): Command {
  if (!isTilePropsTileset(prop.frame.tileset)) {
    throw new Error(`prop ${prop.id} draws ${prop.frame.tileset}, which is not a tile props tileset`);
  }
  const add: Command = { kind: "addProp", mapId, layerId, index, prop };
  const pull = pullFramesCommand(world, prop.frame.tileset, [prop.frame.tile]);
  return pull === null ? add : { kind: "compound", commands: [pull, add] };
}
